(() => {
  const KEY = 'whiteholeWishlistV2';
  const LEGACY_KEY = 'whiteholeWishlistV1';

  const parse = () => {
    try {
      const current = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (Array.isArray(current)) return current;
      const legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || '[]');
      if (Array.isArray(legacy)) {
        localStorage.setItem(KEY, JSON.stringify(legacy));
        return legacy;
      }
    } catch (_) {}
    return [];
  };

  const save = (items) => {
    localStorage.setItem(KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('whitehole:wishlist-updated', { detail: items }));
  };

  const safe = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({
    '&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'
  }[char]));

  const currentVariantId = (button) => {
    const explicit = Number(button.dataset.productVariantId || 0);
    const scope = button.closest('product-info, .product, .wh13-product-card, .card-wrapper, article') || document;
    const selected = Number(scope.querySelector?.('form[action*="/cart/add"] [name="id"]')?.value || 0);
    return selected || explicit || 0;
  };

  class Wishlist {
    constructor() {
      this.drawer = document.querySelector('[data-wishlist-drawer]');
      this.panel = this.drawer?.querySelector('.wh-wishlist__panel');
      this.itemsRoot = this.drawer?.querySelector('[data-wishlist-items]');
      this.empty = this.drawer?.querySelector('[data-wishlist-empty]');
      this.addAllButton = this.drawer?.querySelector('[data-wishlist-add-all]');
      this.status = this.drawer?.querySelector('[data-wishlist-status]');
      this.lastFocus = null;
      this.bind();
      this.refresh();
    }

    setStatus(message = '', isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', isError);
      this.status.hidden = !message;
    }

    bind() {
      document.addEventListener('click', async (event) => {
        const toggle = event.target.closest('[data-wishlist-toggle]');
        if (toggle) {
          event.preventDefault();
          event.stopPropagation();
          this.toggle(toggle);
          return;
        }
        const open = event.target.closest('[data-wishlist-open]');
        if (open) {
          event.preventDefault();
          this.open(open);
          return;
        }
        if (event.target.closest('[data-wishlist-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        const remove = event.target.closest('[data-wishlist-remove]');
        if (remove) {
          event.preventDefault();
          this.remove(remove.dataset.wishlistRemove);
          return;
        }
        const move = event.target.closest('[data-wishlist-move]');
        if (move) {
          event.preventDefault();
          await this.moveOne(move.dataset.wishlistMove, move);
          return;
        }
        if (event.target.closest('[data-wishlist-add-all]')) {
          event.preventDefault();
          await this.moveAll(event.target.closest('[data-wishlist-add-all]'));
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.close();
      });
      document.addEventListener('whitehole:wishlist-updated', () => this.refresh());
      document.addEventListener('shopify:section:load', () => this.refresh());
    }

    toggle(button) {
      const item = {
        handle: button.dataset.productHandle,
        title: button.dataset.productTitle,
        url: button.dataset.productUrl,
        image: button.dataset.productImage,
        price: button.dataset.productPrice,
        variantId: currentVariantId(button),
        available: button.dataset.productAvailable !== 'false'
      };

      let items = parse();
      const exists = items.some((entry) => entry.handle === item.handle);
      items = exists ? items.filter((entry) => entry.handle !== item.handle) : [item, ...items].slice(0, 50);
      save(items);
      this.setStatus(exists ? 'Removed from wishlist.' : 'Saved to wishlist.');
      if (!exists && window.matchMedia('(max-width: 749px)').matches) this.open(button);
    }

    remove(handle) {
      save(parse().filter((item) => item.handle !== handle));
      this.setStatus('Removed from wishlist.');
    }

    async moveOne(handle, button) {
      const item = parse().find((entry) => entry.handle === handle);
      if (!item) return;
      if (!item.variantId || item.available === false) {
        this.setStatus('Open the product and choose an available option first.', true);
        return;
      }
      if (!window.WhiteholeCart?.add) {
        window.location.assign(item.url);
        return;
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.setStatus('Adding to cart…');
      try {
        this.close();
        await window.WhiteholeCart.add([{ id: item.variantId, quantity: 1 }], { trigger: button });
        save(parse().filter((entry) => entry.handle !== handle));
      } catch (err) {
        this.open(button);
        this.setStatus(err?.message || 'This item could not be added to the cart.', true);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    async moveAll(button) {
      const items = parse().filter((item) => item.variantId && item.available !== false);
      if (!items.length) {
        this.setStatus('No available wishlist items can be added yet.', true);
        return;
      }
      if (!window.WhiteholeCart?.add) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.setStatus('Adding wishlist items to cart…');
      try {
        this.close();
        await window.WhiteholeCart.add(items.map((item) => ({ id: item.variantId, quantity: 1 })), { trigger: button });
        const moved = new Set(items.map((item) => item.handle));
        save(parse().filter((item) => !moved.has(item.handle)));
      } catch (err) {
        this.open(button);
        this.setStatus(err?.message || 'The wishlist could not be moved to the cart.', true);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    open(trigger) {
      if (!this.drawer) return;
      this.lastFocus = trigger || document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('wh-drawer-open');
      requestAnimationFrame(() => this.panel?.focus());
    }

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('wh-drawer-open');
      this.lastFocus?.focus?.();
    }

    refresh() {
      const items = parse();
      document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
        count.textContent = items.length;
        count.hidden = items.length === 0;
      });
      document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
        const active = items.some((item) => item.handle === button.dataset.productHandle);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.setAttribute('aria-label', `${active ? 'Remove' : 'Save'} ${button.dataset.productTitle || 'product'} ${active ? 'from' : 'to'} wishlist`);
        const label = button.querySelector('[data-wishlist-label]');
        if (label) label.textContent = active ? 'Saved to wishlist' : 'Save to wishlist';
      });

      if (this.addAllButton) this.addAllButton.hidden = !items.some((item) => item.variantId && item.available !== false);
      if (!this.itemsRoot || !this.empty) return;
      this.empty.hidden = items.length > 0;
      this.itemsRoot.hidden = items.length === 0;
      this.itemsRoot.innerHTML = items.map((item) => {
        const canMove = item.variantId && item.available !== false;
        return `
        <article class="wh-wishlist-item">
          <a class="wh-wishlist-item__image" href="${safe(item.url)}">
            ${item.image ? `<img src="${safe(item.image)}" alt="" width="160" height="160" loading="lazy">` : '<span></span>'}
          </a>
          <div class="wh-wishlist-item__content">
            <a class="wh-wishlist-item__title" href="${safe(item.url)}">${safe(item.title)}</a>
            <p>${safe(item.price)}</p>
            <div class="wh-wishlist-item__actions">
              ${canMove ? `<button class="wh-wishlist-item__move" type="button" data-wishlist-move="${safe(item.handle)}">Move to cart</button>` : `<a href="${safe(item.url)}">Choose options</a>`}
              <button type="button" data-wishlist-remove="${safe(item.handle)}">Remove</button>
            </div>
          </div>
        </article>`;
      }).join('');
    }
  }

  const init = () => new Wishlist();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
